import { Fragment, useSyncExternalStore, type ReactNode } from 'react';

import en from './messages/en.json';
import { format, type Params } from './icu';
import { DEFAULT_LOCALE, findLocale, matchLocale, type Locale } from './locales';

/** Every key of the English catalogue; a key that is not there is a type error. */
export type MessageKey = keyof typeof en;
type Catalogue = Readonly<Record<string, string>>;

const STORAGE_KEY = 'bridge.locale';
const loaders = import.meta.glob<{ readonly default: Catalogue }>(['./messages/*.json', '!./messages/en.json', '!./messages/untranslated.json']);

let current: string = DEFAULT_LOCALE;
let messages: Catalogue = en;
const listeners = new Set<() => void>();

function stored(): string | null {
  try {
    return localStorage.getItem(STORAGE_KEY);
  } catch {
    return null;
  }
}

async function load(code: string): Promise<Catalogue> {
  if (code === DEFAULT_LOCALE) return en;
  const loader = loaders[`./messages/${code}.json`];
  if (loader === undefined) return en;
  return (await loader()).default;
}

function apply(code: string, catalogue: Catalogue): void {
  current = code;
  messages = catalogue;
  const locale = findLocale(code);
  if (typeof document !== 'undefined' && locale) {
    document.documentElement.lang = locale.code;
    document.documentElement.dir = locale.dir;
  }
  listeners.forEach((listener) => {
    listener();
  });
}

/**
 * Picks the language before the first render: the person's saved choice, else the browser's preference, else English.
 * The catalogue is fetched first so the page never flashes in English.
 */
export async function initLocale(): Promise<void> {
  const saved = stored();
  const code = saved !== null && findLocale(saved) ? saved : matchLocale(typeof navigator === 'undefined' ? [] : navigator.languages);
  apply(code, await load(code));
}

/** The person's choice from the language picker; it is kept on this device. */
export async function setLocale(code: string): Promise<void> {
  if (!findLocale(code)) return;
  const catalogue = await load(code);
  try {
    localStorage.setItem(STORAGE_KEY, code);
  } catch {
    // blocked storage: the choice lasts until the page closes
  }
  apply(code, catalogue);
}

export function getLocale(): Locale {
  return findLocale(current) ?? { code: DEFAULT_LOCALE, name: 'English', dir: 'ltr', intl: 'en' };
}

/** The text for `key` in the current language; a key a translation lacks reads in English. */
export function t(key: MessageKey, params: Params = {}): string {
  const message = messages[key] ?? en[key];
  const text = format(getLocale().intl, message, params);
  return getLocale().dir === 'rtl' ? keepSizesWhole(text) : text;
}

/**
 * In a right-to-left sentence a size such as “100 MB” comes apart (the digits join the Arabic, the unit stands on its own
 * and the pair reads “MB 100”). A left-to-right mark before it keeps the pair whole and in order. The mark is added here,
 * on the way out, not written into the catalogues, which hold no hidden characters.
 */
export function keepSizesWhole(text: string): string {
  return text.replace(/(?<![\w.,])(\d+(?:[.,]\d+)?\s?(?:MB|KB|GB)(?![A-Za-z]))/gu, '\u200e$1');
}

type Tags = Readonly<Record<string, (children: ReactNode) => ReactNode>>;

/**
 * Like `t`, with tags in the message made into elements: "Read <link>how it works</link>" with
 * `{ link: (children) => <a href="…">{children}</a> }`. A tag may not nest into itself.
 */
export function tr(key: MessageKey, tags: Tags, params: Params = {}): ReactNode {
  return richText(t(key, params), tags);
}

function richText(text: string, tags: Tags): ReactNode {
  const pattern = /<(\w+)>([\s\S]*?)<\/\1>/u;
  const parts: ReactNode[] = [];
  let rest = text;
  let index = 0;
  for (;;) {
    const found = pattern.exec(rest);
    if (!found) break;
    const [whole = '', name = '', inner = ''] = found;
    const wrap = tags[name];
    if (found.index > 0) parts.push(<Fragment key={`t${String(index)}`}>{rest.slice(0, found.index)}</Fragment>);
    parts.push(<Fragment key={`e${String(index)}`}>{wrap ? wrap(richText(inner, tags)) : inner}</Fragment>);
    rest = rest.slice(found.index + whole.length);
    index += 1;
  }
  if (rest !== '') parts.push(<Fragment key="end">{rest}</Fragment>);
  return parts;
}

function subscribe(listener: () => void): () => void {
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
  };
}

/** The component's way in: it re-renders when the person changes the language. */
export function useLocale(): Locale {
  useSyncExternalStore(subscribe, () => current);
  return getLocale();
}

/** `const t = useT()` — the same `t`, but the component renders again when the language changes. */
export function useT(): typeof t {
  useLocale();
  return t;
}

export { LOCALES } from './locales';
